import { useCallback, useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { PlayerBar, PlayerProvider, usePlayer, formatTime } from '@inscribed/player';
import {
  api,
  contentUrl,
  inscriptionContentUrl,
  type AudioRecord,
  type Project,
  type ProjectApplication,
} from './api';
import { describe, isPlayable, playbackKind, toTrack, titleFor } from './track';
import { ThemeToggle } from './theme';

/**
 * The Inscribed Audio app shell.
 *
 * The one structural decision that matters: `<PlayerProvider>` wraps `<Routes>`, so the player and
 * its queue live outside the router and navigation cannot interrupt playback. Everything else is
 * ordinary.
 */

export function App() {
  const navigate = useNavigate();
  return (
    <PlayerProvider contentUrl={(t) => contentUrl(t.id)}>
      <div className="app">
        <header className="app__head">
          <Link className="app__brand" to="/">
            INSCRIBED.AUDIO
          </Link>
          <span className="app__tag">Listen to Bitcoin.</span>
          <nav className="app__nav">
            <Link to="/audio">Audio</Link>
            <Link to="/identify">Help Identify</Link>
            <Link to="/search">Search</Link>
            {/*
              Leaving for the wallet view. An <a>, not a <Link>: my.inscribed.audio is a separate
              application on its own origin, so this is a document navigation. React Router's
              <Link> would try to resolve it against this router and land on /audio/my.inscribed...,
              which is a not-found page — the exact failure this link exists to prevent.

              Named for what it does rather than for the domain: "My Inscribed Audio" is the site's
              own title, and it is the one destination here that is about the reader's own
              inscriptions instead of the catalogue.
            */}
            <a href="https://my.inscribed.audio" title="Explore the ordinals held in your Bitcoin wallet">
              My Inscribed
            </a>
          </nav>
          {/* The toggle sits at the end of the header, outside the nav: it is a preference about
              the reader's environment, not a place to go. */}
          <ThemeToggle />
        </header>

        <main className="app__body">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/audio" element={<BrowseAudio />} />
            <Route path="/audio/:id" element={<AudioDetail />} />
            <Route path="/block/:height" element={<BlockPage />} />
            <Route path="/projects/:slug" element={<ProjectPage />} />
            <Route path="/identify" element={<Identify />} />
            <Route path="/search" element={<Search />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <PlayerBar onOpenDetail={(t) => navigate(`/audio/${t.id}`)} />
      </div>
    </PlayerProvider>
  );
}

function useAsync<T>(load: () => Promise<T>, deps: unknown[]): { data?: T; error?: string; loading: boolean } {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    load()
      .then((data) => !cancelled && setState({ data, loading: false }))
      .catch((e) => !cancelled && setState({ error: String(e?.message ?? e), loading: false }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

// ---------------------------------------------------------------- shared pieces

/**
 * A featured project, as a rich card.
 *
 * Artwork, a kind label, the project's own description, and how many things are inside it. The
 * artwork is a real inscription fetched through the API's general content route, so the card is
 * showing bytes that exist on Bitcoin rather than a decorative asset.
 *
 * It degrades rather than breaking: no artwork means the token-tinted ground, and a failed image
 * load removes the element instead of leaving a broken-image glyph over the title. That matters
 * because artwork is resolved per project and a project without one is a normal state.
 */
function FeaturedCard({ project }: { project: Project }) {
  const [artFailed, setArtFailed] = useState(false);
  const hasArt = Boolean(project.artwork_ref) && !artFailed;
  // "Tragic Tails"-style subtitles live in the long description for Beat Blocks; the short one is
  // the honest summary for a card.
  const kind = project.media.length > 20 ? 'Generative music · stems' : 'Album · inscribed audio';

  return (
    <Link className={hasArt ? 'card card--has-art' : 'card'} to={`/projects/${project.slug}`}>
      {hasArt && (
        <img
          className="card__art"
          src={inscriptionContentUrl(project.artwork_ref!)}
          alt=""
          loading="lazy"
          onError={() => setArtFailed(true)}
        />
      )}
      <div className="card__body">
        <span className="card__kind">{kind}</span>
        <h3>{project.title}</h3>
        <p>{project.short_description}</p>
        <span className="card__count">
          {project.media.length} playable {project.media.length === 1 ? 'track' : 'items'}
        </span>
      </div>
    </Link>
  );
}

/// Where a row is being rendered. The page states its convention **once**, in prose, rather than
/// each row repeating it — see the note on the `derived` badge below.
type RowVariant = 'catalogue' | 'featured' | 'identify';

/**
 * A duration for display, honest about what it is.
 *
 * `mm:ss` alone renders 45 genuine sub-second clips in this catalogue as **"00:00"** — which reads
 * as a broken or empty file when the row in fact holds 0.12 s of audio. Anything under a second is
 * therefore shown in seconds with two decimals, where the number is unambiguous, and only a true
 * zero is shown as `0s`. The milliseconds must be preserved: rounding them to `00:00` is exactly
 * the kind of label that makes correct data look like a defect.
 */
export function formatDuration(ms: number | undefined): string | undefined {
  if (ms === undefined || !isFinite(ms) || ms < 0) return undefined;
  if (ms === 0) return '0s';
  if (ms < 1000) return `${(ms / 1000).toFixed(2)}s`;
  return formatTime(ms);
}

function RecordRow({
  record,
  onPlay,
  variant = 'catalogue',
}: {
  record: AudioRecord;
  onPlay: (r: AudioRecord) => void;
  variant?: RowVariant;
}) {
  const kind = playbackKind(record);
  const { title, source } = titleFor(record);
  const unknownPct = Math.round((1 - record.identification.identification_score) * 100);
  // Everything a reader can tell two colliding rows apart by without leaving the page. Rows are
  // frequently several inscriptions from one transaction, so the transaction is the honest second
  // half of the identity — and the duration is the first thing anyone browsing audio actually
  // wants. Both come from verified facts.
  const duration = formatDuration(record.probe?.duration_ms ?? record.midi?.duration_ms);
  const shortTxid = record.txid ? record.txid.slice(0, 8) : undefined;

  return (
    <li className="row">
      <button
        className="row__play"
        disabled={kind === 'none'}
        onClick={() => onPlay(record)}
        aria-label={kind === 'none' ? 'nothing to play' : `Play ${title}`}
        title={kind === 'none' ? 'this inscription carries no media' : undefined}
      >
        {kind === 'waveform' ? '▶' : kind === 'module' ? '🎹' : '·'}
      </button>
      <Link className="row__title" to={`/audio/${record.id}`}>
        {title}
      </Link>
      {/*
        Suppressed where it is true of every row.

        A `derived` badge means "nobody has named this". On the catalogue *almost every* row is
        derived, so the badge printed on all 500 sampled rows and became the same kind of constant
        as the "90% unknown" figure removed above — a label identical everywhere is noise that
        reads as an error. It is kept where it is a real distinction: on a featured project page,
        where most stems carry a curated name from the manifest and the derived ones are the
        exception worth flagging.
      */}
      {source === 'derived' && variant !== 'catalogue' && variant !== 'identify' && (
        <span className="badge badge--derived">derived</span>
      )}
      {source === 'community' && <span className="badge badge--community">community</span>}
      {source === 'curated' && <span className="badge badge--curated">curated</span>}
      <span className="row__meta">{describe(record)}</span>
      {duration !== undefined && (
        <span className="row__time">{duration}</span>
      )}
      {record.block_height !== undefined && (
        <Link className="row__block" to={`/block/${record.block_height}`}>
          block {record.block_height}
        </Link>
      )}
      {shortTxid && <span className="row__txid">tx {shortTxid}</span>}
      {record.projects?.map((p) => (
        <span className="badge badge--curated" key={p}>
          {p.replace(/-/g, ' ')}
        </span>
      ))}
      {kind === 'module' && <span className="badge badge--module">module</span>}
      {/*
        Only shown when it says something.
        
        Every record in this catalogue scores the same 0.1, so an unconditional badge printed
        "90% unknown" on all 1,654 rows. A label that is identical everywhere carries no
        information; it just reads as an error. It now appears only when a record is *more*
        identified than the baseline — i.e. someone has actually described it — which is the
        moment the number becomes a fact worth stating. The /identify page, where every row is
        unidentified by construction, states once in prose that the queue is unidentified rather
        than repeating the figure down the list.
      */}
      {unknownPct < 90 && (
        <span
          className="badge badge--unknownness"
          title={`${unknownPct}% of this record is still undescribed; the rest has been`}
        >
          {unknownPct}% unknown
        </span>
      )}
    </li>
  );
}

function usePlayerQueue() {
  const { dispatch } = usePlayer();
  return useCallback(
    (records: AudioRecord[], index: number) => {
      // Only offer what can actually be played. Queuing a `no_media` record would put a broken
      // item in the transport and make the player look faulty.
      const playable = records.filter(isPlayable);
      if (playable.length === 0) return;
      // `index` is an index into `records`, not into `playable`. Mapping to tracks and passing it
      // through would play the wrong item whenever an unplayable record sits earlier in the list:
      // filtering shifts every subsequent position. So the position is **computed** against the
      // filtered list, by counting how many playable records precede the one clicked.
      const target = records[index];
      const position = target
        ? playable.reduce((n, r) => n + (records.indexOf(r) < index ? 1 : 0), 0)
        : 0;
      dispatch({
        type: 'playNow',
        tracks: playable.map(toTrack),
        index: Math.min(position, playable.length - 1),
      });
    },
    [dispatch],
  );
}

// ---------------------------------------------------------------- pages

function Home() {
  const play = usePlayerQueue();
  const projects = useAsync<{ projects: Project[] }>(api.projects, []);
  const latest = useAsync(() => api.audio({ order: 'latest', limit: 12 }), []);

  return (
    <>
      <section className="hero">
        <h1>Listen to Bitcoin.</h1>
        <p className="hero__sub">
          Audio permanently inscribed on Bitcoin — {latest.data?.total ?? '…'} known inscriptions,
          identified and verified against the chain.
        </p>
      </section>

      <section>
        <h2>Featured</h2>
        <div className="cards">
          {projects.loading && <p>Loading…</p>}
          {projects.error && <p className="error">{projects.error}</p>}
          {(projects.data?.projects ?? []).map((p) => (
            <FeaturedCard key={p.slug} project={p} />
          ))}
        </div>
      </section>

      <section>
        <h2>Latest</h2>
        <ul className="rows">
          {(latest.data?.records ?? []).map((r) => (
            <RecordRow
              key={r.id}
              record={r}
              onPlay={() => play(latest.data!.records, latest.data!.records.indexOf(r))}
            />
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * The filter panel.
 *
 * Modelled on my.inscribed.audio's mymedia page, where every content type present is a labelled
 * checkbox with its count, plus a reset. Two deliberate departures, both because this catalogue is
 * audio-only:
 *
 * * The counts come from the API's own vocabulary (`/v1/formats`), so the filter can only offer
 *   formats that exist. A hardcoded list would drift the first time a record of a new kind lands.
 * * The `bitmaps` grouping mymedia uses to bucket image types has no meaning here, so it is absent
 *   rather than present-and-empty.
 *
 * These are real checkboxes with real labels, as they are there: a screen reader announces
 * "audio/ogg, 311", which a row of styled buttons does not.
 */
function FormatCards({
  formats,
  active,
  onToggle,
  total,
  onReset,
}: {
  formats: { format: string; count: number }[];
  active: string | null;
  onToggle: (format: string | null) => void;
  total: number;
  onReset: () => void;
}) {
  if (formats.length === 0) return null;
  return (
    <div className="format-cards" role="group" aria-label="Filter by format">
      <label className={active === null ? 'format-card format-card--on' : 'format-card'}>
        <input type="checkbox" checked={active === null} onChange={() => onToggle(null)} />
        <span className="format-card__name">All</span>
        <span className="format-card__count">{total.toLocaleString()}</span>
      </label>
      {formats.map((f) => (
        <label
          key={f.format}
          className={active === f.format ? 'format-card format-card--on' : 'format-card'}
        >
          <input type="checkbox" checked={active === f.format} onChange={() => onToggle(f.format)} />
          <span className="format-card__name">{f.format.replace(/^audio\//, '')}</span>
          <span className="format-card__count">{f.count.toLocaleString()}</span>
        </label>
      ))}
      {active !== null && (
        <button className="format-card format-card--reset" onClick={onReset}>
          reset filter
        </button>
      )}
    </div>
  );
}

/** The sum of the per-format counts, which is the size of the whole playable catalogue. */
function FORMATS_TOTAL(formats: Record<string, number>): number {
  return Object.values(formats).reduce((a, b) => a + b, 0);
}

function BrowseAudio() {
  const play = usePlayerQueue();
  const { dispatch } = usePlayer();
  const params = new URLSearchParams(window.location.search);
  const [format, setFormat] = useState(params.get('format') ?? '');
  const [order, setOrder] = useState<'latest' | 'earliest'>(params.get('order') === 'earliest' ? 'earliest' : 'latest');
  const [showUnplayable, setShowUnplayable] = useState(params.get('playable') === 'false');
  const [page, setPage] = useState(0);
  const perPage = 100;

  const formats = useAsync(api.formats, []);

  // Reset to the first page whenever the query changes — otherwise a filter change leaves the
  // reader on page 7 of a result set that no longer exists.
  useEffect(() => {
    setPage(0);
  }, [format, order, showUnplayable]);

  const listing = useAsync(
    () =>
      api.audio({
        format: format || undefined,
        order,
        limit: perPage,
        offset: page * perPage,
        playable: showUnplayable ? undefined : true,
      }),
    [format, order, showUnplayable, page],
  );

  const records = listing.data?.records ?? [];
  const total = listing.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      <h2>Browse audio</h2>

      <FormatCards
        formats={Object.entries(formats.data ?? {}).map(([format, count]) => ({ format, count }))}
        active={format || null}
        onToggle={(f) => setFormat(f ?? '')}
        total={FORMATS_TOTAL(formats.data ?? {})}
        onReset={() => setFormat('')}
      />

      <div className="toolbar">
        <label className="toggle">
          <input
            type="checkbox"
            checked={order === 'earliest'}
            onChange={(e) => setOrder(e.target.checked ? 'earliest' : 'latest')}
          />
          oldest first
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showUnplayable}
            onChange={(e) => setShowUnplayable(e.target.checked)}
          />
          {/* Records that declare a media type but carry no media are shown when asked for, and
              are never given a play control. */}
          include records with nothing to play
        </label>
        <button
          onClick={() => dispatch({ type: 'playNow', tracks: records.filter(isPlayable).map(toTrack), index: 0 })}
          disabled={records.filter(isPlayable).length === 0}
        >
          ▶ Play all {records.filter(isPlayable).length}
        </button>
      </div>

      <p className="note dim">
        {total.toLocaleString()} record{total === 1 ? '' : 's'}
        {format ? ` matching ${format}` : ''}
        {total > 0 && <> · page {page + 1} of {pages}</>}
      </p>
      <p className="note dim">
        Names shown as <em>Audio at Block N · id</em> are derived from verified facts, not chosen
        by anyone: the block says when it was inscribed and the id distinguishes it from any other
        inscription in that block. Where a curator or the project itself has named a record, that
        name is used and labelled.
      </p>

      <ul className="rows">
        {records.map((r, i) => (
          <RecordRow key={r.id} record={r} onPlay={() => play(records, i)} variant="catalogue" />
        ))}
      </ul>

      {pages > 1 && (
        <nav className="pager" aria-label="Pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            ← Previous
          </button>
          <span>
            {page * perPage + 1}–{Math.min((page + 1) * perPage, total)} of {total.toLocaleString()}
          </span>
          <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </nav>
      )}
    </>
  );
}

function AudioDetail() {
  const { id = '' } = useParams();
  const play = usePlayerQueue();
  const { dispatch } = usePlayer();
  const rec = useAsync(() => api.record(id), [id]);

  if (rec.loading) return <p>Loading…</p>;
  if (rec.error || !rec.data) return <p className="error">{rec.error ?? 'not found'}</p>;

  const r = rec.data;
  const kind = playbackKind(r);
  const { title, source } = titleFor(r);
  const duration = r.probe?.duration_ms ?? r.midi?.duration_ms;
  const mislabel = r.mismatch_class === 'declared_type_mismatch';
  const alias = r.mismatch_class === 'container_alias';
  const noMedia = r.mismatch_class === 'no_media';

  return (
    <>
      <h2>
        {title}{' '}
        {source === 'derived' && <span className="badge badge--derived">derived name</span>}
        {source === 'community' && <span className="badge badge--community">community name</span>}
        {source === 'curated' && <span className="badge badge--curated">curated name</span>}
      </h2>

      {r.projects && r.projects.length > 0 && (
        <p className="membership">
          Part of{' '}
          {r.projects.map((slug, i) => (
            <span key={slug}>
              {i > 0 && ', '}
              <Link to={`/projects/${slug}`}>{slug.replace(/-/g, ' ')}</Link>
            </span>
          ))}
          <span className="badge badge--curated">curated collection</span>
        </p>
      )}

      <div className="split">
        <section className="verified">
          <h3>Verified on Bitcoin</h3>
          <dl>
            <dt>Format</dt>
            <dd>{r.detected_container ?? r.declared_content_type}</dd>

            <dt>Declared as</dt>
            <dd>
              {r.declared_content_type}
              {/* A disagreement is a provenance fact, not a warning. Nothing is corrupt: the
                  bytes are exactly what they are, and the label is exactly what was declared. */}
              {mislabel && <span className="badge badge--fact"> differs from the bytes</span>}
              {alias && <span className="badge badge--fact"> container alias</span>}
            </dd>

            {r.detected_container &&
              r.detected_content_type &&
              r.detected_content_type !== r.detected_container && (
                <>
                  <dt>Detected by signature</dt>
                  <dd>{r.detected_content_type}</dd>
                </>
              )}

            {r.effective_content_type && r.effective_content_type !== r.detected_container && (
              <>
                <dt>Effective type</dt>
                <dd>
                  {r.effective_content_type}
                  <span className="badge badge--fact"> after content-encoding</span>
                </dd>
              </>
            )}

            {r.probe?.codec_name && (
              <>
                <dt>Codec</dt>
                <dd>{r.probe.codec_name}</dd>
              </>
            )}
            {duration !== undefined && (
              <>
                <dt>Duration</dt>
                <dd>{(duration / 1000).toFixed(2)}s</dd>
              </>
            )}
            {r.probe?.sample_rate && (
              <>
                <dt>Sample rate</dt>
                <dd>{r.probe.sample_rate} Hz</dd>
              </>
            )}
            {r.probe?.channels && (
              <>
                <dt>Channels</dt>
                <dd>{r.probe.channels}</dd>
              </>
            )}
            {r.probe?.bitrate && (
              <>
                <dt>Bitrate</dt>
                <dd>{Math.round(r.probe.bitrate / 1000)} kbps</dd>
              </>
            )}
            {r.midi && (
              <>
                <dt>MIDI</dt>
                <dd>
                  format {r.midi.format}, {r.midi.tracks} track{r.midi.tracks === 1 ? '' : 's'},{' '}
                  {r.midi.division} PPQN
                </dd>
              </>
            )}
            {r.byte_length !== undefined && (
              <>
                <dt>Size</dt>
                <dd>{r.byte_length.toLocaleString()} bytes</dd>
              </>
            )}
            <dt>Probe</dt>
            <dd>
              {r.probe_status.replace(/_/g, ' ')}
              {r.probe_status === 'identified_without_decoder' && (
                <span className="badge badge--fact"> read without a decoder</span>
              )}
            </dd>
            {r.probe_error && (
              <>
                <dt>Probe error</dt>
                <dd className="mono small">{r.probe_error}</dd>
              </>
            )}
            {r.block_height !== undefined && (
              <>
                <dt>Block</dt>
                <dd>
                  <Link to={`/block/${r.block_height}`}>{r.block_height}</Link>
                </dd>
              </>
            )}
            {r.txid && (
              <>
                <dt>Transaction</dt>
                <dd className="mono">{r.txid}</dd>
              </>
            )}
            <dt>Inscription</dt>
            <dd className="mono">{r.id}</dd>
            {r.content_sha256 && (
              <>
                <dt>SHA-256</dt>
                <dd className="mono">{r.content_sha256}</dd>
              </>
            )}
          </dl>

          {kind === 'waveform' && <button onClick={() => play([r], 0)}>▶ Play</button>}
          {kind === 'module' && (
            <p className="note">
              Identified and inspected, but a browser cannot decode this format — so there is no
              play button. The metadata above is read from the file itself, not from a decoder.
            </p>
          )}
          {kind === 'none' && (
            <p className="note">
              {/* The typed-pointer case, stated rather than implied. */}
              {noMedia
                ? 'This inscription declares a media type and carries no media of its own. Nothing is claimed here that it is playable.'
                : 'This container is not one the player can open, so no play control is offered.'}
            </p>
          )}

          <p className="note dim">
            Embedded on Bitcoin · direct inscription body
            {r.projects && r.projects.length > 0
              ? ' · this record is a member of a curated project'
              : ''}
          </p>
        </section>

        <section className="community">
          <h3>Community</h3>
          {r.community.contribution_count === 0 ? (
            <p className="note">
              Nobody has described this inscription yet.{' '}
              <Link to="/identify">Help identify Bitcoin's audio →</Link>
            </p>
          ) : (
            <>
              <p>{r.community.contribution_count} contribution(s)</p>
              {Object.entries(r.community.tallies ?? {}).map(([field, tally]) => (
                <div key={field}>
                  <h4>{field.replace(/_/g, ' ')}</h4>
                  <ul>
                    {tally.map((t) => (
                      <li key={t.value}>
                        {t.value} — {t.votes} confirmation{t.votes === 1 ? '' : 's'}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
          {/* Contribution writes are not enabled in this slice. Saying so is better than a form
              that silently fails. */}
          <p className="note dim">Contributions open after the player release.</p>
        </section>
      </div>
    </>
  );
}

/**
 * Renders an inscription the way its bytes ask to be rendered.
 *
 * The mymedia card always uses an `<iframe>`, which is right for an inscription that is an
 * *application* and wrong for one that is a still image: an AVIF or a WebP has no document to lay
 * out, so the iframe fills the frame with empty space and the picture sits in a corner of it. Both
 * artwork inscriptions here are images, and the first attempt framed a square 1280×1280 AVIF in a
 * landscape box and produced exactly that — a wide black rectangle with a small emblem at the left
 * edge.
 *
 * So the container is chosen by **what the bytes are**, sniffed from the response's own
 * `Content-Type` rather than guessed from the id:
 *
 * * `image/*` → an `<img>`. The frame then takes the picture's own aspect ratio, so a square
 *   artwork is a square card and there is no dead space to fill.
 * * anything else → a sandboxed `<iframe>`, as the reference does, for a document that must run.
 *
 * `sandbox="allow-scripts"` withholds `allow-same-origin` on purpose: the document inside is
 * inscribed by a third party, and same-origin access would let it reach into this origin.
 */
function InscriptionFrame({ id, title }: { id: string; title: string }) {
  const [state, setState] = useState<{ kind: 'probing' | 'image' | 'document' | 'failed' }>({
    kind: 'probing',
  });

  useEffect(() => {
    let live = true;
    setState({ kind: 'probing' });
    fetch(inscriptionContentUrl(id), { method: 'HEAD' })
      .then((r) => {
        if (!live) return;
        const type = (r.headers.get('Content-Type') ?? '').toLowerCase();
        setState({ kind: type.startsWith('image/') ? 'image' : 'document' });
      })
      .catch(() => {
        // A failed probe is not a reason to show nothing: fall back to the iframe, which is what a
        // non-image inscription needs anyway.
        if (live) setState({ kind: 'document' });
      });
    return () => {
      live = false;
    };
  }, [id]);

  if (state.kind === 'failed') {
    return <p className="note dim">This inscription could not be loaded.</p>;
  }
  if (state.kind === 'probing') {
    return <div className="app-card__placeholder" aria-hidden="true" />;
  }
  if (state.kind === 'image') {
    return (
      <img
        className="app-card__image"
        src={inscriptionContentUrl(id)}
        alt={`Artwork for ${title}`}
        onError={() => setState({ kind: 'failed' })}
      />
    );
  }
  return (
    <iframe
      src={inscriptionContentUrl(id)}
      title={`${title} inscription`}
      sandbox="allow-scripts"
      loading="lazy"
      allowFullScreen
    />
  );
}

function ProjectPage() {
  const { slug = '' } = useParams();
  const play = usePlayerQueue();
  const { dispatch } = usePlayer();
  const project = useAsync(() => api.project(slug), [slug]);

  if (project.loading) return <p>Loading…</p>;
  if (project.error || !project.data) return <p className="error">{project.error ?? 'not found'}</p>;

  const p = project.data;

  return (
    <>
      <h2>{p.title}</h2>
      <p className="lede">{p.long_description ?? p.short_description}</p>

      {/*
        The project's own inscription, rendered — the display pattern from my.inscribed.audio's
        mymedia page, where a card leads with an iframe of the inscription itself rather than with
        a description of it. The point is that the artwork and any generative behaviour are shown
        live, as executed from chain bytes, instead of being summarised.

        `sandbox` is set and `allow-same-origin` is deliberately withheld: the document inside is
        inscribed by a third party, and giving it same-origin access would let it reach into this
        origin.
      */}
      {p.artwork_ref && (
        <div className="app-card">
          <div className="app-card__frame">
            <InscriptionFrame id={p.artwork_ref} title={p.title} />
          </div>
          <div className="app-card__meta">
            <h3>{p.title}</h3>
            <p>{p.short_description}</p>
            <div className="app-card__actions">
              <a
                className="app-card__link"
                href={inscriptionContentUrl(p.artwork_ref)}
                target="_blank"
                rel="noreferrer"
              >
                Open inscription ↗
              </a>
              {/* An in-app route, so it must go through the router: a plain anchor here would do
                  a document load and tear down the player mid-track. */}
              <Link className="app-card__link" to={`/audio/${p.artwork_ref}`} title={p.artwork_ref}>
                {p.artwork_ref.slice(0, 10)}…
              </Link>
            </div>
          </div>
        </div>
      )}

      <p className="provenance">
        <strong>How these were found:</strong> {p.provenance}
      </p>

      {/* What the project's application inscription actually contains, as opposed to what it is
          assumed to contain. */}
      {p.application && <ApplicationReport app={p.application} />}

      <div className="toolbar">
        <button
          onClick={() => dispatch({ type: 'playNow', tracks: p.media.filter(isPlayable).map(toTrack), index: 0 })}
          disabled={p.media.length === 0}
        >
          ▶ Play all {p.media.length}
        </button>
      </div>

      {p.unresolved && p.unresolved.length > 0 && (
        <p className="note warn">
          {p.unresolved.length} member(s) could not be resolved to a playable record and are
          reported rather than hidden.
        </p>
      )}

      <ul className="rows">
        {p.media.map((r, i) => (
          <RecordRow key={r.id} record={r} onPlay={() => play(p.media, i)} variant="featured" />
        ))}
      </ul>
    </>
  );
}

/**
 * What a project's application inscription contains.
 *
 * This is the report the handoff's "dig into the HTML and show its media elements" resolves to for
 * these projects, and it is worth being precise about why it is mostly *not* a list of media.
 *
 * The Beat Blocks genesis is a p5.js sketch. Its markup declares **no** media elements — the sound
 * is generated at runtime and seeded from the inscription's own index — so there is nothing to
 * enumerate, and a component that rendered an empty gallery would imply a failure that did not
 * happen. What is true and useful is: it is an application, it declares no media, it generates its
 * output at runtime, and it loads these six inscriptions in these roles.
 *
 * The three states are kept visibly distinct: **not scanned** (we could not read it), **scanned,
 * no media** (looked, found none), and **scanned, media found** (listed). Collapsing any two of
 * them is how this becomes a lie.
 */
function ApplicationReport({ app }: { app: ProjectApplication }) {
  if (!app.scanned) {
    return (
      <p className="note warn">
        The project's application inscription could not be read, so nothing is claimed about what it
        contains. It is not that it holds no media — it is that this page does not know.
      </p>
    );
  }

  const media = app.media ?? [];
  const refs = app.references ?? [];
  const byRole = refs.reduce<Record<string, number>>((acc, r) => {
    acc[r.role] = (acc[r.role] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section className="app-report">
      <h3>What the application inscription contains</h3>
      <p className="note dim">
        Read from the inscription's own bytes
        {app.content_type ? ` (${app.content_type}, ${app.byte_length.toLocaleString()} B)` : ''}.
      </p>

      {app.generates_at_runtime && (
        <>
          <p className="note">
            This is a <strong>generative application</strong>: it loads a rendering runtime and builds
            its output when it runs, so its media is not written in its markup. There is nothing in the
            HTML to list, which is why nothing is listed below.
          </p>
          <p className="note dim">
            It also <strong>will not execute through this API</strong>. An inscribed app loads its
            sibling inscriptions by the ord-server path — <code>/content/&lt;id&gt;</code>, without the{' '}
            <code>/v1</code> prefix — and this service serves content at{' '}
            <code>/v1/content/&lt;id&gt;</code>. So the app fetches its own scripts and gets a 404. The
            card above therefore shows the project's <em>image</em> artwork; it is not evidence that the
            application ran. Serving ord's paths is a routing decision, not a bug, and it has not been
            made.
          </p>
        </>
      )}

      {media.length > 0 ? (
        <>
          <h4>Media elements declared</h4>
          <ul className="app-report__list">
            {media.map((m, i) => (
              <li key={`${m.tag}-${i}`}>
                <span className="badge badge--module">{m.tag}</span>
                <code>{m.src}</code>
                {m.declared_type && <span className="dim"> · {m.declared_type}</span>}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="note dim">
          The markup declares <strong>no</strong> audio, video or image elements at all.
        </p>
      )}

      {refs.length > 0 && (
        <>
          <h4>
            Inscriptions it loads{' '}
            <span className="dim">
              ({refs.length}
              {Object.keys(byRole).length > 0 &&
                ': ' +
                  Object.entries(byRole)
                    .map(([role, n]) => `${n} ${role}`)
                    .join(', ')}
              )
            </span>
          </h4>
          <p className="note dim">
            These are reachable, which is not the same as being media. A script is code and a
            manifest is data; only the rows marked <em>media</em> are media.
          </p>
          <ul className="app-report__list">
            {refs.map((r) => (
              <li key={r.id}>
                <span className={`badge badge--${r.role === 'media' ? 'curated' : 'derived'}`}>
                  {r.role}
                </span>
                <code>{r.id}</code>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="note dim">
        Application inscription{' '}
        <code className="dim">{app.inscription_id.slice(0, 16)}…</code> ·{' '}
        <a href={inscriptionContentUrl(app.inscription_id)} target="_blank" rel="noreferrer">
          open it ↗
        </a>
      </p>
    </section>
  );
}

function Identify() {
  const play = usePlayerQueue();
  const least = useAsync(() => api.identify(50), []);

  return (
    <>
      <h2>Help identify Bitcoin's audio</h2>
      <p className="lede">
        These are the inscriptions we know least about — verified on chain, but nobody has said
        what they are. There are only a couple of thousand audio inscriptions on Bitcoin, so
        identifying them is tractable.
      </p>
      <p className="note dim">
        The score is how little is known about a record, not how good it is. A record with no
        title, no artist, and no classification sits at the bottom; one a curator has described
        sits at the top. Everything below is chain-verified either way — what is missing is only
        description.
      </p>
      <p className="note dim">
        Every row on this page is unidentified, and every name below is derived — that is what put
        them here — so neither the figure nor a "derived" label is repeated beside each one. Naming
        any of them is the contribution this page exists to collect.
      </p>
      <ul className="rows">
        {(least.data?.records ?? []).map((r, i) => (
          <RecordRow key={r.id} record={r} onPlay={() => play(least.data!.records, i)} variant="identify" />
        ))}
      </ul>
      <p className="note dim">Contributions open after the player release.</p>
    </>
  );
}

function Search() {
  const play = usePlayerQueue();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(params.get('q') ?? '');
  const [submitted, setSubmitted] = useState(params.get('q') ?? '');
  const results = useAsync(
    () => (submitted ? api.search(submitted) : Promise.resolve({ query: '', exact: false, returned: 0, records: [] })),
    [submitted],
  );

  const hits = results.data?.records ?? [];

  return (
    <>
      <h2>Search</h2>
      <p className="lede">
        An inscription id, a transaction id, a block height, a body SHA-256, a project, or a title
        someone has given it. Exact identifiers jump straight to the record.
      </p>
      <form
        className="search"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q);
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="inscription id / txid / block / sha256 / project / title"
          aria-label="Search"
        />
        <button type="submit">Search</button>
      </form>

      {submitted && results.data && (
        <p className="note dim">
          {results.data.returned} result{results.data.returned === 1 ? '' : 's'}
          {results.data.exact ? ' · exact identifier' : ''}
        </p>
      )}

      <ul className="rows">
        {hits.map((r, i) => (
          <li className="row" key={r.id}>
            <button
              className="row__play"
              disabled={!isPlayable(r)}
              onClick={() => play(hits, i)}
              aria-label={isPlayable(r) ? `Play ${titleFor(r).title}` : 'nothing to play'}
            >
              {playbackKind(r) === 'waveform' ? '▶' : playbackKind(r) === 'module' ? '🎹' : '·'}
            </button>
            <Link className="row__title" to={`/audio/${r.id}`}>
              {titleFor(r).title}
            </Link>
            <span className="badge badge--reason">matched {r.matched_by.replace(/_/g, ' ')}</span>
            <span className="row__meta">{describe(r)}</span>
            {r.projects?.map((p) => (
              <span className="badge badge--curated" key={p}>
                {p.replace(/-/g, ' ')}
              </span>
            ))}
          </li>
        ))}
      </ul>

      {submitted && results.data?.exact && hits.length === 1 && (
        <p className="note">
          <button onClick={() => navigate(`/audio/${hits[0].id}`)}>Open {hits[0].id.slice(0, 12)}… →</button>
        </p>
      )}
    </>
  );
}

/** A block's audio, as the block page. */
function BlockPage() {
  const { height = '' } = useParams();
  const play = usePlayerQueue();
  const block = useAsync(
    () => (height ? api.block(Number(height)) : Promise.reject(new Error('no height'))),
    [height],
  );

  if (block.loading) return <p>Loading…</p>;
  if (block.error || !block.data) return <p className="error">{block.error ?? 'not found'}</p>;

  const b = block.data;

  return (
    <>
      <h2>Block {b.height.toLocaleString()}</h2>
      <p className="lede">
        {b.audio_count === 0
          ? 'No audio inscription in this block is in the catalog.'
          : `${b.audio_count} audio inscription${b.audio_count === 1 ? '' : 's'} found in this block.`}
      </p>

      {b.records.length > 0 && (
        <div className="toolbar">
          <button
            onClick={() => {
              const playable = b.records.filter(isPlayable);
              if (playable.length > 0) {
                play(b.records, b.records.findIndex(isPlayable));
              }
            }}
            disabled={b.records.filter(isPlayable).length === 0}
          >
            ▶ Play all {b.records.filter(isPlayable).length}
          </button>
        </div>
      )}

      <ul className="rows">
        {b.records.map((r, i) => (
          <RecordRow key={r.id} record={r} onPlay={() => play(b.records, i)} />
        ))}
      </ul>
      {b.records.length === 0 && (
        <p className="note dim">
          Blocks before the first audio inscription, and blocks whose audio has not been discovered
          yet, are indistinguishable here — this catalog covers the audio it has found.
        </p>
      )}
    </>
  );
}

function NotFound() {
  return (
    <>
      <h2>Not found</h2>
      <p>
        <Link to="/">Back to listening</Link>
      </p>
    </>
  );
}
